import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import AuthService from './services/auth';
import Login from './components/Login';
import TopNavbar from './components/TopNavbar';
import Dashboard from './components/Dashboard';
import AssetTypes from './components/AssetTypes';
import Assets from './components/Assets';
import Employees from './components/Employees';
import AssetAssignments from './components/AssetAssignments';
import Repairs from './components/Repairs';
import Departments from './components/Departments';
import Discoms from './components/Discoms';
import Vendors from './components/Vendors';
import Users from './components/Users';
import AiSearchChat from './components/AiSearchChat';
import AuditLogs from './components/AuditLogs';
import RepairAnalytics from './components/RepairAnalytics';
import Help from './components/Help';
import SecuritySettings from './components/SecuritySettings';
import Footer from './components/Footer';
import Loader from './components/Loader';
import ReceiptVerification from './components/ReceiptVerification';
import './components/Loader.css';

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
}

function AppContent() {
  const { login, hasPermission, canAccessPage } = useAuth();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTwoFactorPrompt, setShowTwoFactorPrompt] = useState(false);

  useEffect(() => {
    const currentUser = AuthService.getCurrentUser();
    if (currentUser) {
      setUser(currentUser);
      AuthService.setupAxiosInterceptors();
    }
    setTimeout(() => setLoading(false), 500);
  }, []);

  useEffect(() => {
    if (!user) {
      setShowTwoFactorPrompt(false);
      return;
    }

    const dismissalKey = `twoFactorPromptDismissed:${user.username}`;
    const dismissed = sessionStorage.getItem(dismissalKey) === 'true';
    setShowTwoFactorPrompt(Boolean(user.twoFactorSetupRequired && !dismissed));
  }, [user]);

  const handleLogin = () => {
    const currentUser = AuthService.getCurrentUser();
    setUser(currentUser);
    login(currentUser);
  };

  const handleUserUpdate = (updatedUser) => {
    setUser(updatedUser);
    login(updatedUser);
  };

  const handleLogout = () => {
    if (user?.username) {
      sessionStorage.removeItem(`twoFactorPromptDismissed:${user.username}`);
    }
    AuthService.logout();
    setUser(null);
  };

  const dismissTwoFactorPrompt = () => {
    if (user?.username) {
      sessionStorage.setItem(`twoFactorPromptDismissed:${user.username}`, 'true');
    }
    setShowTwoFactorPrompt(false);
  };

  if (loading) {
    return <Loader overlay={true} text="Initializing Saral Asset Manager..." size="large" />;
  }

  if (window.location.pathname.startsWith('/receipt-verification/')) {
    return (
      <Router>
        <Routes>
          <Route path="/receipt-verification/:receiptNumber" element={<ReceiptVerification />} />
        </Routes>
      </Router>
    );
  }

  if (!user) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <Router>
      <div className="d-flex flex-column" style={{height: '100vh', overflow: 'hidden'}}>
        <TopNavbar onLogout={handleLogout} user={user} />
        <div className="main-content flex-grow-1" style={{overflow: 'auto', minHeight: 0}}>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/profile/security" element={<SecuritySettings onUserUpdated={handleUserUpdate} />} />
            {canAccessPage('canManageAssets') && <Route path="/asset-types" element={<AssetTypes />} />}
            {canAccessPage('canManageAssets') && <Route path="/departments" element={<Departments />} />}
            {canAccessPage('canManageAssets') && <Route path="/discoms" element={<Discoms />} />}
            {canAccessPage('canManageAssets') && <Route path="/vendors" element={<Vendors />} />}
            {canAccessPage('canManageAssets') && <Route path="/assets" element={<Assets />} />}
            {canAccessPage('canManageEmployees') && <Route path="/employees" element={<Employees />} />}
            {canAccessPage('canManageAssignments') && <Route path="/assignments" element={<AssetAssignments />} />}
            {canAccessPage('canManageRepairs') && <Route path="/repairs" element={<Repairs />} />}
            {canAccessPage('canManageUsers') && <Route path="/users" element={<Users />} />}
            {canAccessPage('canManageUsers') && <Route path="/ai-search" element={<AiSearchChat />} />}
            {user?.role === 'Admin' && <Route path="/audit-logs" element={<AuditLogs />} />}
            <Route path="/help" element={<Help />} />
          </Routes>
        </div>
        <Footer />
      </div>

      {showTwoFactorPrompt && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.65)', zIndex: 2000 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-shield-alt me-2"></i>Enable Two-Factor Authentication
                </h5>
                <button type="button" className="btn-close" onClick={dismissTwoFactorPrompt}></button>
              </div>
              <div className="modal-body">
                <p className="mb-2">Two-factor authentication is not set up on this account yet.</p>
                <p className="text-muted mb-0">You can enable it now for better protection, or ignore this for now and continue using the app.</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={dismissTwoFactorPrompt}>
                  Ignore for now
                </button>
                <Link className="btn btn-primary" to="/profile/security" onClick={dismissTwoFactorPrompt}>
                  Set Up 2FA
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </Router>
  );
}

export default App;
